// Account modal and CRUD operations (part 2 of accounts module)

import { auth } from '../core/auth.js';
import { apiCall } from '../core/api.js';
import { showError, showSuccess } from '../core/ui.js';
import { getCurrentUser } from '../utils/helpers.js';
import { loadAccountsData } from './accounts.js';
import { t } from '../core/i18n.js';

/**
 * Enable/disable the SmartTime + Time Budget sub-checkboxes based on the
 * master "Enable Time Tracking" toggle. `prefix` is 'account' (create) or 'edit'.
 */
window.toggleAccountTimeTracking = function (prefix) {
    const master = document.getElementById(`${prefix}-enable-timetracking`);
    const sub = document.getElementById(`${prefix}-timetracking-subfeatures`);
    if (!master || !sub) return;
    const on = master.checked;
    sub.classList.toggle('opacity-50', !on);
    sub.querySelectorAll('input[type="checkbox"]').forEach(cb => { cb.disabled = !on; });
};

/**
 * Load parent account tree for create modal
 */
export async function loadParentAccountTree() {
    try {
        const response = await apiCall('/accounts/hierarchy', {
            method: 'GET',
            headers: auth.getAuthHeaders()
        });
        
        const treeContainer = document.getElementById('parent-account-tree-container');
        if (!treeContainer) return;
        
        if (response && response.accounts_tree && response.accounts_tree.length > 0) {
            treeContainer.innerHTML = renderParentAccountTree(response.accounts_tree);
        } else {
            treeContainer.innerHTML = `<div class="text-gray-400 text-sm">${t('accounts.noAccountsAvailable')}</div>`;
        }
    } catch (error) {
        console.error('Failed to load parent account tree:', error);
        const treeContainer = document.getElementById('parent-account-tree-container');
        if (treeContainer) {
            treeContainer.innerHTML = `<div class="text-red-500 text-sm">${t('accounts.errorLoadingAccounts')}</div>`;
        }
    }
}

/**
 * Render parent account tree
 */
export function renderParentAccountTree(accounts, level = 0) {
    let html = '';
    
    accounts.forEach(account => {
        const accountId = account._id || account.id;
        const accountName = account.account_name || account.name || 'Unnamed Account';
        const hasChildren = account.children && account.children.length > 0;
        const indentPadding = level * 20;
        
        html += `
            <div class="account-tree-item" data-level="${level}">
                <div class="flex items-center py-1.5 hover:bg-gray-50 rounded" style="padding-left: ${indentPadding}px;">
                    ${hasChildren ? `
                        <button type="button" onclick="toggleParentAccountTreeItem('parent-tree-${accountId}')" 
                                class="mr-1 text-gray-400 hover:text-gray-600 flex-shrink-0">
                            <svg id="parent-tree-icon-${accountId}" class="w-4 h-4 transform transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                            </svg>
                        </button>
                    ` : '<span class="w-5 mr-1"></span>'}
                    <label class="flex items-center cursor-pointer flex-1">
                        <input type="radio" name="parent-account-selection" value="${accountId}" 
                               onchange="selectParentAccount('${accountId}', ${level})"
                               class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded">
                        <span class="ml-2 text-sm text-gray-700">${accountName}</span>
                    </label>
                </div>
                ${hasChildren ? `
                    <div id="parent-tree-${accountId}" class="hidden">
                        ${renderParentAccountTree(account.children, level + 1)}
                    </div>
                ` : ''}
            </div>
        `;
    });
    
    return html;
}

/**
 * Toggle parent account tree item
 */
export function toggleParentAccountTreeItem(itemId) {
    const item = document.getElementById(itemId);
    const iconId = itemId.replace('parent-tree-', 'parent-tree-icon-');
    const icon = document.getElementById(iconId);
    
    if (item && icon) {
        item.classList.toggle('hidden');
        icon.classList.toggle('rotate-90');
    }
}

/**
 * Select parent account.
 * @param {string} accountId - selected parent account id
 * @param {number} treeLevel - depth in the hierarchy tree (0 = root Docospot account)
 */
export function selectParentAccount(accountId, treeLevel = null) {
    const hiddenInput = document.getElementById('parent-account-id');
    if (hiddenInput) {
        hiddenInput.value = accountId;
    }

    // The Tasks feature flag is only relevant for top-level accounts, i.e. those
    // created directly under the root Docospot account (tree level 0).
    const tasksContainer = document.getElementById('tasks-feature-container');
    if (tasksContainer) {
        const isTopAccountParent = treeLevel === 0;
        tasksContainer.classList.toggle('hidden', !isTopAccountParent);
        if (!isTopAccountParent) {
            const cb = document.getElementById('account-enable-tasks');
            if (cb) cb.checked = false;
        }
    }
}

/**
 * Create new account
 */
export async function createAccount() {
    const form = document.getElementById('create-account-form');
    if (!form) return;

    const formData = new FormData(form);
    
    // Check if user is SuperAdmin
    const currentUser = getCurrentUser();
    const isSuperAdmin = currentUser && currentUser.role === 'SuperAdmin';
    
    // Build account data based on role permissions
    const accountData = {
        account_name: formData.get('account_name'),
        contact_email: ''  // Backend requires this, but we can make it optional/empty
    };

    // Add parent_account_id if selected
    const parentId = formData.get('parent_id');
    if (parentId) {
        accountData.parent_account_id = parentId;
    }

    // Only SuperAdmin can set these fields
    if (isSuperAdmin) {
        const accountType = formData.get('account_type');
        if (accountType) {
            accountData.account_type = accountType;
        }
        
        const status = formData.get('status');
        if (status) {
            accountData.status = status;
        }
        
        const maxUsers = formData.get('max_users');
        if (maxUsers && maxUsers.trim() !== '') {
            accountData.max_users = parseInt(maxUsers);
        }

        // Tasks feature flag (only shown when creating a top-level account)
        const tasksContainer = document.getElementById('tasks-feature-container');
        if (tasksContainer && !tasksContainer.classList.contains('hidden')) {
            const enableTasks = document.getElementById('account-enable-tasks');
            accountData.enabled_features = enableTasks && enableTasks.checked ? ['tasks'] : [];

            // SmartTime / Time Budget are on by default; record which are turned off
            const enableTimeTracking = document.getElementById('account-enable-timetracking');
            const enableSmartTime = document.getElementById('account-enable-smarttime');
            const enableTimeBudget = document.getElementById('account-enable-timebudget');
            const disabled = [];
            if (enableTimeTracking && !enableTimeTracking.checked) disabled.push('timetracking');
            if (enableSmartTime && !enableSmartTime.checked) disabled.push('smarttime');
            if (enableTimeBudget && !enableTimeBudget.checked) disabled.push('time_budget');
            accountData.disabled_features = disabled;
        }
    }

    try {
        await apiCall('/accounts/', {
            method: 'POST',
            headers: {
                ...auth.getAuthHeaders(),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(accountData)
        });

        showSuccess(t('accounts.createSuccess'));
        window.closeModal();
        loadAccountsData();
    } catch (error) {
        showError(t('accounts.createError') + ': ' + (error.message || 'Ukendt fejl'));
    }
}

/**
 * Show edit account modal
 */
export async function editAccount(accountId) {
    try {
        // Fetch account details
        const account = await apiCall(`/accounts/${accountId}`, {
            method: 'GET',
            headers: auth.getAuthHeaders()
        });

        const modal = document.getElementById('modal');
        if (!modal) return;

        // Check if user is SuperAdmin
        const currentUser = getCurrentUser();
        const isSuperAdmin = currentUser && currentUser.role === 'SuperAdmin';
        
        // Generate the modal HTML
        const modalHtml = `
            <div class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                <div class="relative top-20 mx-auto p-5 border w-11/12 md:w-96 shadow-lg rounded-md bg-white">
                    <div class="mt-3">
                        <h3 class="text-lg font-medium text-gray-900 mb-4">${t('accounts.edit')}</h3>
                        <form id="edit-account-form" class="space-y-4">
                            <input type="hidden" id="edit-account-id" value="${accountId}">
                            <div>
                                <label for="edit-account-name" class="block text-sm font-medium text-gray-700">${t('accounts.name')} <span class="text-red-500">*</span></label>
                                <input type="text" id="edit-account-name" name="account_name" required
                                       value="${account.account_name || ''}"
                                       class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                       placeholder="Indtast kontonavn">
                            </div>
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-2">${t('accounts.parent')}</label>
                                <div class="text-xs text-gray-500 mb-2">${t('accounts.parentAccountDesc')}</div>
                                <div id="edit-parent-account-tree-container" class="border border-gray-300 rounded-md p-3 max-h-64 overflow-y-auto bg-white">
                                    <div class="text-gray-400 text-sm">${t('accounts.loading')}</div>
                                </div>
                                <input type="hidden" id="edit-parent-account-id" name="parent_id" value="${account.parent_account_id || ''}">
                            </div>
                            ${isSuperAdmin ? `
                            <div>
                                <label for="edit-account-type" class="block text-sm font-medium text-gray-700">${t('accounts.accountType')}</label>
                                <select id="edit-account-type" name="account_type"
                                        class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                                    <option value="Enterprise" ${account.account_type === 'Enterprise' ? 'selected' : ''}>Enterprise</option>
                                    <option value="Business" ${account.account_type === 'Business' ? 'selected' : ''}>Business</option>
                                    <option value="Standard" ${account.account_type === 'Standard' ? 'selected' : ''}>Standard</option>
                                </select>
                            </div>
                            <div>
                                <label for="edit-account-status" class="block text-sm font-medium text-gray-700">${t('common.status')}</label>
                                <select id="edit-account-status" name="status"
                                        class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                                    <option value="Active" ${account.status === 'Active' ? 'selected' : ''}>${t('common.active')}</option>
                                    <option value="Inactive" ${account.status === 'Inactive' ? 'selected' : ''}>${t('common.inactive')}</option>
                                    <option value="Suspended" ${account.status === 'Suspended' ? 'selected' : ''}>${t('accounts.statusSuspended')}</option>
                                </select>
                            </div>
                            <div>
                                <label for="edit-max-users" class="block text-sm font-medium text-gray-700">${t('accounts.maxUsersOptional')}</label>
                                <input type="number" id="edit-max-users" name="max_users" min="1"
                                       value="${account.max_users || ''}"
                                       class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                       placeholder="${t('accounts.maxUsersPlaceholder')}">
                            </div>
                            ${account.level === 1 ? `
                            <div class="border-t border-gray-200 pt-4 space-y-3">
                                <label class="flex items-center cursor-pointer">
                                    <input type="checkbox" id="edit-enable-tasks" name="enable_tasks"
                                           ${(account.enabled_features || []).includes('tasks') ? 'checked' : ''}
                                           class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded">
                                    <span class="ml-2 text-sm font-medium text-gray-700">Enable Tasks feature</span>
                                </label>
                                <label class="flex items-center cursor-pointer">
                                    <input type="checkbox" id="edit-enable-timetracking" name="enable_timetracking"
                                           ${(account.disabled_features || []).includes('timetracking') ? '' : 'checked'}
                                           onchange="window.toggleAccountTimeTracking && window.toggleAccountTimeTracking('edit')"
                                           class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded">
                                    <span class="ml-2 text-sm font-medium text-gray-700">${t('accounts.enableTimeTracking')}</span>
                                </label>
                                <div id="edit-timetracking-subfeatures" class="ml-6 space-y-2">
                                    <label class="flex items-center cursor-pointer">
                                        <input type="checkbox" id="edit-enable-smarttime" name="enable_smarttime"
                                               ${(account.disabled_features || []).includes('smarttime') ? '' : 'checked'}
                                               class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded">
                                        <span class="ml-2 text-sm text-gray-700">${t('accounts.enableSmartTime')}</span>
                                    </label>
                                    <label class="flex items-center cursor-pointer">
                                        <input type="checkbox" id="edit-enable-timebudget" name="enable_timebudget"
                                               ${(account.disabled_features || []).includes('time_budget') ? '' : 'checked'}
                                               class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded">
                                        <span class="ml-2 text-sm text-gray-700">${t('accounts.enableTimeBudget')}</span>
                                    </label>
                                </div>
                                <p class="text-xs text-gray-500 mt-1">${t('accounts.topLevelFeaturesHint')}</p>
                            </div>
                            ` : ''}
                            ` : `
                            <div>
                                <label class="block text-sm font-medium text-gray-700">${t('accounts.accountType')}</label>
                                <div class="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                                    ${account.account_type || 'Standard'}
                                </div>
                            </div>
                            <div>
                                <label class="block text-sm font-medium text-gray-700">${t('common.status')}</label>
                                <div class="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                                    ${account.status || 'Active'}
                                </div>
                            </div>
                            <div>
                                <label class="block text-sm font-medium text-gray-700">${t('accounts.maxUsersLabel')}</label>
                                <div class="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                                    ${account.max_users || t('accounts.unlimited')}
                                </div>
                            </div>
                            `}
                        </form>
                        <div class="flex items-center justify-end space-x-3 mt-6">
                            <button onclick="closeModal()" 
                                    class="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400">
                                ${t('common.cancel')}
                            </button>
                            <button onclick="updateAccount()" 
                                    class="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700">
                                ${t('accounts.updateButton')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        `;
        
        modal.innerHTML = modalHtml;
        modal.classList.remove('hidden');
        // Reflect initial Time Tracking master state on its sub-features
        if (window.toggleAccountTimeTracking) window.toggleAccountTimeTracking('edit');
        
        // Load parent account hierarchy tree for editing
        await loadEditParentAccountTree(accountId, account.parent_account_id);
    } catch (error) {
        showError('Kunne ikke indlæse kontooplysninger: ' + (error.message || 'Ukendt fejl'));
    }
}

/**
 * Load parent account tree for edit modal
 */
async function loadEditParentAccountTree(currentAccountId, selectedParentId) {
    try {
        const response = await apiCall('/accounts/hierarchy', {
            method: 'GET',
            headers: auth.getAuthHeaders()
        });
        
        const treeContainer = document.getElementById('edit-parent-account-tree-container');
        if (!treeContainer) return;
        
        if (response && response.accounts_tree && response.accounts_tree.length > 0) {
            // Filter out the current account and its descendants
            const filteredAccounts = filterAccountTree(response.accounts_tree, currentAccountId);
            treeContainer.innerHTML = renderEditParentAccountTree(filteredAccounts, selectedParentId);
        } else {
            treeContainer.innerHTML = `<div class="text-gray-400 text-sm">${t('accounts.noAccountsAvailableShort')}</div>`;
        }
    } catch (error) {
        console.error('Failed to load parent account tree:', error);
    }
}

/**
 * Filter account tree to exclude current account
 */
function filterAccountTree(accounts, excludeAccountId) {
    const filtered = [];
    
    for (const account of accounts) {
        const accountId = account._id || account.id;
        
        if (accountId === excludeAccountId) continue;
        
        const filteredAccount = { ...account };
        if (account.children && account.children.length > 0) {
            filteredAccount.children = filterAccountTree(account.children, excludeAccountId);
        }
        
        filtered.push(filteredAccount);
    }
    
    return filtered;
}

/**
 * Render edit parent account tree
 */
function renderEditParentAccountTree(accounts, selectedParentId, level = 0) {
    let html = '';
    
    accounts.forEach(account => {
        const accountId = account._id || account.id;
        const accountName = account.account_name || account.name || 'Unnamed Account';
        const hasChildren = account.children && account.children.length > 0;
        const indentPadding = level * 20;
        const isSelected = accountId === selectedParentId;
        
        html += `
            <div class="account-tree-item" data-level="${level}">
                <div class="flex items-center py-1.5 hover:bg-gray-50 rounded" style="padding-left: ${indentPadding}px;">
                    ${hasChildren ? `
                        <button type="button" onclick="toggleEditParentAccountTreeItem('edit-parent-tree-${accountId}')" 
                                class="mr-1 text-gray-400 hover:text-gray-600 flex-shrink-0">
                            <svg id="edit-parent-tree-icon-${accountId}" class="w-4 h-4 transform transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                            </svg>
                        </button>
                    ` : '<span class="w-5 mr-1"></span>'}
                    <label class="flex items-center cursor-pointer flex-1">
                        <input type="radio" name="edit-parent-account-selection" value="${accountId}" 
                               ${isSelected ? 'checked' : ''}
                               onchange="selectEditParentAccount('${accountId}')"
                               class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded">
                        <span class="ml-2 text-sm text-gray-700">${accountName}</span>
                    </label>
                </div>
                ${hasChildren ? `
                    <div id="edit-parent-tree-${accountId}" class="hidden">
                        ${renderEditParentAccountTree(account.children, selectedParentId, level + 1)}
                    </div>
                ` : ''}
            </div>
        `;
    });
    
    return html;
}

/**
 * Toggle edit parent account tree item
 */
export function toggleEditParentAccountTreeItem(itemId) {
    const item = document.getElementById(itemId);
    const iconId = itemId.replace('edit-parent-tree-', 'edit-parent-tree-icon-');
    const icon = document.getElementById(iconId);
    
    if (item && icon) {
        item.classList.toggle('hidden');
        icon.classList.toggle('rotate-90');
    }
}

/**
 * Select edit parent account
 */
export function selectEditParentAccount(accountId) {
    const hiddenInput = document.getElementById('edit-parent-account-id');
    if (hiddenInput) {
        hiddenInput.value = accountId;
    }
}

/**
 * Update account
 */
export async function updateAccount() {
    const form = document.getElementById('edit-account-form');
    if (!form) return;

    const accountId = document.getElementById('edit-account-id').value;
    const formData = new FormData(form);
    
    const currentUser = getCurrentUser();
    const isSuperAdmin = currentUser && currentUser.role === 'SuperAdmin';
    
    const accountData = {
        account_name: formData.get('account_name')
    };

    const parentId = formData.get('parent_id');
    if (parentId) {
        accountData.parent_account_id = parentId;
    }

    if (isSuperAdmin) {
        const accountType = formData.get('account_type');
        if (accountType) accountData.account_type = accountType;
        
        const status = formData.get('status');
        if (status) accountData.status = status;
        
        const maxUsers = formData.get('max_users');
        if (maxUsers && maxUsers.trim() !== '') {
            accountData.max_users = parseInt(maxUsers);
        }

        // Tasks feature flag (only rendered for top-level accounts)
        const enableTasks = document.getElementById('edit-enable-tasks');
        if (enableTasks) {
            accountData.enabled_features = enableTasks.checked ? ['tasks'] : [];

            // SmartTime / Time Budget are on by default; record which are turned off
            const enableTimeTracking = document.getElementById('edit-enable-timetracking');
            const enableSmartTime = document.getElementById('edit-enable-smarttime');
            const enableTimeBudget = document.getElementById('edit-enable-timebudget');
            const disabled = [];
            if (enableTimeTracking && !enableTimeTracking.checked) disabled.push('timetracking');
            if (enableSmartTime && !enableSmartTime.checked) disabled.push('smarttime');
            if (enableTimeBudget && !enableTimeBudget.checked) disabled.push('time_budget');
            accountData.disabled_features = disabled;
        }
    }

    try {
        await apiCall(`/accounts/${accountId}`, {
            method: 'PUT',
            headers: {
                ...auth.getAuthHeaders(),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(accountData)
        });

        showSuccess(t('accounts.updateSuccess'));
        window.closeModal();
        loadAccountsData();
    } catch (error) {
        showError(t('accounts.updateError') + ': ' + (error.message || 'Ukendt fejl'));
    }
}

export default {
    loadParentAccountTree,
    renderParentAccountTree,
    toggleParentAccountTreeItem,
    selectParentAccount,
    createAccount,
    editAccount,
    toggleEditParentAccountTreeItem,
    selectEditParentAccount,
    updateAccount
};
